import {SegmentedControl, ActionList, ActionMenu, CounterLabel, IconButton, TextInput} from '@primer/react'
import {use, type SetStateAction} from 'react'
import {CommentsContext} from './CommentsContext'
import type {AuthorFilterItem} from './utils/get-author-filters'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SearchIcon, FilterIcon, SortAscIcon, SortDescIcon, ListUnorderedIcon} from '@primer/octicons-react'

import styles from './CommentsViewControls.module.css'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {FeatureFlags} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {supportsCodeOrderSort, ViewMode} from './utils/view-mode'
import {defaultFilterStates, type CommentsFilterState} from './use-comments-filter-state'
import {CommentsSort} from './use-comments-sort-state'

interface CommentsViewControlsProps {
  /** All authors in the current view mode. */
  authorList: AuthorFilterItem[]
  /** Current filter state, for the current view mode. */
  filterState: CommentsFilterState
  setFilterState: (state: SetStateAction<CommentsFilterState>) => void
  sort: CommentsSort
  setSort: (sort: SetStateAction<CommentsSort>) => void
  viewMode: ViewMode
  className?: string
}

function isDefaultFilterControls(viewMode: ViewMode, filterState: CommentsFilterState): boolean {
  const defaultState = defaultFilterStates()[viewMode]
  return (
    filterState.hideOutdatedThreads === defaultState.hideOutdatedThreads &&
    filterState.hideResolvedThreads === defaultState.hideResolvedThreads &&
    filterState.hideHiddenComments === defaultState.hideHiddenComments &&
    (filterState.showAgents ?? true) === (defaultState.showAgents ?? true) &&
    arraysEqual(filterState.selectedAuthors, defaultState.selectedAuthors)
  )
}

function arraysEqual(first?: string[], second?: string[]) {
  if (first === second) return true
  if (!first || !second || first.length !== second.length) return false
  return first.every((value, index) => value === second[index])
}

function FilterInput({filterState, setFilterState}: Pick<CommentsViewControlsProps, 'filterState' | 'setFilterState'>) {
  const updateFilterText = (filterText: string) => setFilterState(prev => ({...prev, filterText}))

  return (
    <TextInput
      block
      aria-label="Filter comments"
      leadingVisual={SearchIcon}
      placeholder="Filter comments"
      value={filterState.filterText}
      className={styles.input}
      onChange={event => updateFilterText(event.target.value)}
    />
  )
}

function FilterMenu({
  authorList,
  filterState,
  setFilterState,
}: Pick<CommentsViewControlsProps, 'authorList' | 'filterState' | 'setFilterState'>) {
  const {viewMode} = use(CommentsContext)

  const toggleResolvedFilter = () => setFilterState(prev => ({...prev, hideResolvedThreads: !prev.hideResolvedThreads}))

  const toggleOutdatedFilter = () => setFilterState(prev => ({...prev, hideOutdatedThreads: !prev.hideOutdatedThreads}))

  const toggleHiddenFilter = () => setFilterState(prev => ({...prev, hideHiddenComments: !prev.hideHiddenComments}))

  const toggleAgents = () => setFilterState(prev => ({...prev, showAgents: !(prev.showAgents ?? true)}))

  const visibleAuthors = filterState.showAgents === false ? authorList.filter(author => !author.isAgent) : authorList

  const onSelectAuthor = (authorLogin: string) => {
    setFilterState(previous => {
      const selectedAuthors = new Set(previous.selectedAuthors ?? [])
      if (selectedAuthors.has(authorLogin)) selectedAuthors.delete(authorLogin)
      else selectedAuthors.add(authorLogin)

      return {
        ...previous,
        selectedAuthors: selectedAuthors.size === 0 ? undefined : [...selectedAuthors],
      }
    })
  }

  const isFilteringActive = !isDefaultFilterControls(viewMode, filterState)
  const hideReviewThreadFilters =
    !isFeatureEnabled('prs_docked_comments_replies') && viewMode !== ViewMode.ThreadPreviews

  return (
    <div className="position-relative">
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            icon={FilterIcon}
            aria-label={
              isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
            }
            description="Additional comment filters"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList>
            <ActionList.Group selectionVariant="multiple">
              {!hideReviewThreadFilters && (
                <>
                  <ActionList.Item
                    selected={!filterState.hideResolvedThreads}
                    onSelect={event => {
                      event.preventDefault()
                      toggleResolvedFilter()
                    }}
                  >
                    Show resolved comments
                  </ActionList.Item>
                  <ActionList.Item
                    selected={!filterState.hideOutdatedThreads}
                    onSelect={event => {
                      event.preventDefault()
                      toggleOutdatedFilter()
                    }}
                  >
                    Show outdated comments
                  </ActionList.Item>
                  <ActionList.Item selected={!filterState.hideHiddenComments} onSelect={() => toggleHiddenFilter()}>
                    Show hidden comments
                  </ActionList.Item>
                </>
              )}
              <ActionList.Item
                selected={filterState.showAgents !== false}
                onSelect={event => {
                  event.preventDefault()
                  toggleAgents()
                }}
              >
                Show AI and bot comments
              </ActionList.Item>
              <ActionList.Divider />
            </ActionList.Group>
            <ActionList.Group selectionVariant="multiple">
              <ActionList.GroupHeading>Filter by author</ActionList.GroupHeading>

              {visibleAuthors.map(author => (
                <ActionList.Item
                  key={author.login}
                  selected={filterState.selectedAuthors?.includes(author.login) ?? false}
                  onSelect={event => {
                    event.preventDefault()
                    onSelectAuthor(author.login)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <GitHubAvatar src={author.avatarUrl} />
                  </ActionList.LeadingVisual>
                  {author.login}
                  {author.count > 0 && (
                    <ActionList.TrailingVisual>
                      <CounterLabel>{author.count}</CounterLabel>
                    </ActionList.TrailingVisual>
                  )}
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
    </div>
  )
}

const sortConfigs = {
  [CommentsSort.NewestFirst]: {
    shortLabel: 'Newest',
    label: 'Newest first',
    Icon: SortDescIcon,
  },
  [CommentsSort.OldestFirst]: {
    shortLabel: 'Oldest',
    label: 'Oldest first',
    Icon: SortAscIcon,
  },
  [CommentsSort.DiffOrder]: {
    shortLabel: 'Code order',
    label: 'Reviews in code order',
    Icon: ListUnorderedIcon,
  },
} as const

function SortMenu({
  sort: currentSort,
  setSort,
  viewMode,
}: Pick<CommentsViewControlsProps, 'sort' | 'setSort' | 'viewMode'>) {
  const {Icon: CurrenSortIcon, shortLabel: currentSortLabel} = sortConfigs[currentSort]

  const availableSorts = supportsCodeOrderSort(viewMode)
    ? [CommentsSort.NewestFirst, CommentsSort.OldestFirst, CommentsSort.DiffOrder]
    : [CommentsSort.NewestFirst, CommentsSort.OldestFirst]

  return (
    <ActionMenu>
      <ActionMenu.Button leadingVisual={CurrenSortIcon} aria-label={`Sort comments: ${currentSortLabel}`}>
        {currentSortLabel}
      </ActionMenu.Button>

      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {availableSorts.map(sort => {
            const {label, Icon} = sortConfigs[sort]
            return (
              <ActionList.Item key={sort} selected={sort === currentSort} onSelect={() => setSort(sort)}>
                <ActionList.LeadingVisual>
                  <Icon />
                </ActionList.LeadingVisual>
                {label}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function CommentsViewSwitch() {
  const {viewMode, setViewMode} = use(CommentsContext)

  return isFeatureEnabled('prs_docked_comments_replies') ? (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextMode = [ViewMode.IssueComments, ViewMode.ThreadPreviews, ViewMode.Mine][value]
        if (nextMode) setViewMode(nextMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>All</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Unresolved</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.Mine}>Mine</SegmentedControl.Button>
    </SegmentedControl>
  ) : (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextViewMode = value === 0 ? ViewMode.ThreadPreviews : ViewMode.IssueComments
        setViewMode(nextViewMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Review</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>Conversation</SegmentedControl.Button>
    </SegmentedControl>
  )
}

/**
 * Renders view controls (filter, sort, etc) for comments.
 */
export function CommentsViewControls(props: CommentsViewControlsProps) {
  return (
    <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
      <div className={clsx(styles.container, props.className)}>
        <CommentsViewSwitch />
        {!isFeatureEnabled('prs_docked_comments_replies') && <FilterInput {...props} />}
        <div className={styles.controls}>
          <FilterMenu {...props} />
          {isFeatureEnabled('prs_docked_comments_replies') && <SortMenu {...props} />}
        </div>
      </div>
    </FeatureFlags>
  )
}
